/**
 * AdvisorResultCard - web用スタイリングラッパー
 *
 * @kyumo/shared-ui の AdvisorResultView に web 固有の
 * Markdown 描画（MarkdownContent）・モデル/usage 表示を適用。
 */

import { type AdvisorSuccessResult, getTotalTokens, getUsageBreakdown } from '@kyumo/common';
import { AdvisorResultView } from '@kyumo/shared-ui';
import { formatCost, formatNumber } from './chart-utils';
import { MarkdownContent } from './MarkdownContent';

interface AdvisorUsage {
  inputTokens: number;
  outputTokens: number;
  reasoningTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  cost?: number;
  costs?: { total: number };
}

/** 管理アプリ用の拡張 Advisor 成功結果型（詳細なトークン情報を含む） */
interface ExtendedAdvisorSuccessResult extends AdvisorSuccessResult {
  usage?: AdvisorUsage;
}

export interface AdvisorResultCardProps {
  result: unknown;
  /** Executor が Advisor に渡した相談内容 */
  question?: string | undefined;
}

export function AdvisorResultCard({ result, question }: AdvisorResultCardProps) {
  return (
    <AdvisorResultView
      result={result}
      question={question}
      className="mt-2"
      renderAdvice={(advice) => <MarkdownContent content={advice} />}
      renderMeta={(r) => {
        const usage = (r as ExtendedAdvisorSuccessResult).usage;
        const costTotal = usage?.costs?.total ?? usage?.cost;
        return (
          usage && (
            <>
              <span>{formatNumber(getTotalTokens(usage))} tokens</span>
              {costTotal !== undefined && (
                <span className="text-yellow-400">{formatCost(costTotal)}</span>
              )}
            </>
          )
        );
      }}
      renderFooter={(r) => {
        const usage = (r as ExtendedAdvisorSuccessResult).usage;
        if (!usage) return null;
        // 内訳として並べるため、入力はキャッシュ分、出力は推論分を除いて表示する
        const breakdown = getUsageBreakdown(usage);
        return (
          <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-2 text-xs text-muted-foreground">
            {r.advisorModelName && (
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono">{r.advisorModelName}</span>
            )}
            <span>入力: {formatNumber(breakdown.input)}</span>
            <span>出力: {formatNumber(breakdown.output)}</span>
            {breakdown.reasoning > 0 && <span>推論: {formatNumber(breakdown.reasoning)}</span>}
            {breakdown.cacheRead > 0 && (
              <span>キャッシュ読込: {formatNumber(breakdown.cacheRead)}</span>
            )}
            {breakdown.cacheWrite > 0 && (
              <span>キャッシュ書込: {formatNumber(breakdown.cacheWrite)}</span>
            )}
          </div>
        );
      }}
    />
  );
}
